import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOSchema from '../../components/SEOSchema';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [expandedPartner, setExpandedPartner] = useState<number | null>(null);
  const [activeService, setActiveService] = useState<number | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const services = [
    {
      icon: 'ri-lightbulb-line',
      title: 'Business Strategy & Execution',
      description: 'Refine strategic direction and sharpen execution for sustainable growth'
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Financial Planning & Investment Readiness',
      description: 'Structure finances and prepare for investor engagement'
    },
    {
      icon: 'ri-search-eye-line',
      title: 'Market Analysis & Competitive Positioning',
      description: 'Deep market intelligence and strategic positioning guidance'
    },
    {
      icon: 'ri-settings-3-line',
      title: 'Lean Operations & Early-Stage Support',
      description: 'Operational excellence for startups and scaling companies'
    },
    {
      icon: 'ri-megaphone-line',
      title: 'Marketing Strategy & Brand Differentiation',
      description: 'Build distinctive brands and effective go-to-market strategies'
    },
    {
      icon: 'ri-calculator-line',
      title: 'Accounting & Financial Structuring',
      description: 'Sound financial architecture and accounting frameworks'
    },
    {
      icon: 'ri-team-line',
      title: 'Talent Management & Organizational Development',
      description: 'Build high-performing teams and organizational capabilities'
    },
    {
      icon: 'ri-refresh-line',
      title: 'Turnaround Consulting',
      description: 'Navigate challenges and restore performance for troubled businesses'
    }
  ];

  const caseStudies = [
    {
      title: 'Crystal Jade – U.S. Market Entry',
      lead: 'Darlene Chiu Bryant',
      image: 'https://public.readdy.ai/ai/img_res/6704a2cea024532ccf8a194cf9083f76.jpg',
      overview: 'Supported Crystal Jade Group\'s first expansion outside Asia, guiding U.S. market entry strategy, operations, and cultural adaptation.',
      contributions: [
        'Delivered comprehensive U.S. market analysis on trends, consumers, and competitors',
        'Identified and vetted joint venture partners',
        'Arranged targeted meetings with potential JV and investment partners',
        'Connected with key U.S. service providers (legal, real estate, branding, HR)',
        'Provided ongoing market intelligence and cross-cultural guidance',
        'Advised on menu localization and ingredient sourcing'
      ]
    },
    {
      title: 'YCurb – Smart Curb Management Platform',
      lead: 'Carlos Serrano-Quan',
      image: 'https://readdy.ai/api/search-image?query=modern%20urban%20smart%20city%20street%20with%20advanced%20technology%20infrastructure%20featuring%20clean%20organized%20curb%20management%20digital%20sensors%20and%20intelligent%20traffic%20systems%20in%20bright%20daylight%20with%20contemporary%20architecture%20and%20efficient%20urban%20planning%20visualization&width=480&height=320&seq=ycurb-smart-city&orientation=landscape',
      overview: 'Served as strategic advisor and early investor to YCurb, supporting product development and municipal pilot deployment.',
      contributions: [
        'Guided product-market fit and pilot design aligned with city operations',
        'Structured pilot scope, deliverables, and success metrics',
        'Translated technical features into clear business value',
        'Supported execution and iterative refinement based on client feedback'
      ],
      outcomes: [
        'Three successful pilots in the San Francisco Bay Area',
        'Three RFP wins across the U.S. and Canada; three active contracts',
        'Continuing advisory support as YCurb scales its municipal footprint'
      ]
    }
  ];

  const partners = [
    {
      name: 'Carlos Serrano-Quan',
      title: 'CEO, Sequoia Commercial Group',
      image: 'https://static.readdy.ai/image/33013d7d17c179c408c300a791de0fd9/2d1e782509a03a3da60185ad3d19ce68.jpeg',
      bio: [
        'CEO, Sequoia Commercial Group; President, San Mateo Planning Commission',
        'Leadership roles: Past President & Chair CREAA, Board Member, Silicon Valley AREAA, GlobalSF, National Asian Pacific Center on Aging; President, San Francisco Pretrial',
        'Social enterprise entrepreneur and angel investor; advisor to YCurb and other startups',
        'CoStar Commercial Real Estate Power Broker Award recipient',
        'Former appointee to SF Department of Aging & Adult Services and Department of Children, Youth & Families advisory committees',
        'U.S. Army Disabled Veteran',
        'Nonprofit leadership: HomeownershipSF, Community Youth Center, Chinese Newcomers Service Center, American Diabetes Association, Goodwill Industries'
      ]
    },
    {
      name: 'Darlene Chiu Bryant',
      title: 'Founder & Executive Director, GlobalSF',
      image: 'https://static.readdy.ai/image/33013d7d17c179c408c300a791de0fd9/0704939f79bd7b0a4b806b09436e9d70.jpeg',
      bio: [
        'Founder & Executive Director, GlobalSF',
        'Former Executive Director, ChinaSF; helped attract $5.1B+ in foreign direct investment',
        'Career includes global supply chain roles in Asia and public affairs roles for Mayor Gavin Newsom, East West Bank, UCB, and PG&E',
        'Founding Board President, AAPI Lead',
        'Co-Founder & President, Asian Pacific American Leadership Foundation and CALNET',
        'Board Member, USF Center for Asia Pacific Studies',
        'Former SF Small Business Commissioner',
        'Recognized as one of the most influential women in Bay Area business (SF Business Times)'
      ]
    },
    {
      name: 'Regina Huang',
      title: 'Real Estate Investment Consultant',
      image: 'https://static.readdy.ai/image/33013d7d17c179c408c300a791de0fd9/de62c4e3a1349e3e1662fd5d7a531096.jpeg',
      bio: [
        'Real estate investment consultant specializing in Bay Area residential & commercial transactions (since 2015)',
        'Former sales & marketing manager, Xinshang Catering (Shanghai)',
        'Lived 20+ years in Shanghai and Guangdong; fluent Cantonese and Mandarin',
        'Immigrated to the U.S. after earning a bachelor\'s degree from East China Normal University',
        'Volunteer, Chinese Newcomers Service Center'
      ]
    },
    {
      name: 'Andy Bryant',
      title: 'Executive Leader & Turnaround Specialist',
      image: 'https://static.readdy.ai/image/33013d7d17c179c408c300a791de0fd9/9775cbf23c317b46034c96930b30dfd6.jpeg',
      bio: [
        'Executive leader specializing in financial turnarounds, operational improvement, & organizational growth',
        'Former COO, Self-Help for the Elderly: led financial turnaround and secured CARF accreditation',
        'Former CEO, Lifemark Group: led rebranding and diversification, increasing valuation 2.4x',
        'Experience stabilizing startups & improving health program efficiency through automation and process redesign',
        'Past leadership: Self-Help for the Elderly, Cemetery & Mortuary Association of California, & CA Cemetery & Funeral Bureau Advisory Committee, Presbyterian Deacon',
        'Lead volunteer teams serving meals to San Francisco neighbors in need for various charities'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* SEO Schema */}
      <SEOSchema type="organization" />
      <SEOSchema type="website" />
      <SEOSchema type="service" />

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <header className="flex items-center">
            <h1 className={isScrolled ? 'block' : 'hidden'}>
              <img src="https://public.readdy.ai/ai/img_res/a3e9a384-3fd2-411f-b961-06e957112ff2.png" alt="Access Point Advisors LLC" className="h-12" />
            </h1>
            <div className={isScrolled ? 'hidden' : 'block'}>
              <img src="https://public.readdy.ai/ai/img_res/a3e9a384-3fd2-411f-b961-06e957112ff2.png" alt="Access Point Advisors LLC" className="h-12" />
            </div>
          </header>
          <div className="hidden md:flex items-center space-x-8">
            {['About', 'Services', 'Case Studies', 'Team', 'Strategic Networks', 'Contact'].map((item) => (
              <button
                key={item}
                onClick={() => scrollToSection(item.toLowerCase().replace(' ', '-'))}
                className={`text-sm font-medium transition-colors duration-300 hover:text-[#c9a961] relative group ${isScrolled ? 'text-gray-800' : 'text-white'}`}
              >
                {item}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#c9a961] transition-all duration-300 group-hover:w-full"></span>
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative h-screen flex items-center">
        <div className="absolute inset-0">
          <img 
            src="https://readdy.ai/api/search-image?query=abstract%20professional%20business%20network%20visualization%20with%20interconnected%20golden%20lines%20and%20nodes%20representing%20strategic%20connections%20and%20partnerships%20against%20deep%20charcoal%20background%20with%20subtle%20gradient%20creating%20sophisticated%20corporate%20atmosphere%20and%20sense%20of%20global%20reach&width=1920&height=1080&seq=hero-network&orientation=landscape" 
            alt="Strategic Networks" 
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <div className="max-w-3xl">
            <div className="w-16 h-1 bg-[#c9a961] mb-6"></div>
            <h1 className="text-6xl md:text-7xl font-serif font-bold text-white leading-tight mb-6">
              Strategic Guidance for Sustainable Growth
            </h1>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              Navigating complexity. Strengthening performance. Unlocking opportunity.
            </p>
            <button 
              onClick={() => scrollToSection('about')}
              className="group px-8 py-4 border-2 border-white text-white rounded-lg hover:bg-[#c9a961] hover:border-[#c9a961] transition-all duration-300 flex items-center space-x-2 whitespace-nowrap"
            >
              <span className="font-medium">Explore Our Approach</span>
              <i className="ri-arrow-right-line text-xl transition-transform duration-300 group-hover:translate-x-1"></i>
            </button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-[#f8f7f5]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-12">
            <div className="text-xs uppercase tracking-widest text-[#c9a961] mb-4 font-medium">About Access Point Advisors LLC</div>
            <h2 className="text-5xl font-serif font-bold text-gray-900 mb-8">The Go-To Firm for Strategic Advice</h2>
          </div>
          
          <div className="grid md:grid-cols-5 gap-12">
            <div className="md:col-span-3">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Access Point Advisors LLC is the go-to firm for strategic advice on growing your company. Our experts provide guidance, operational excellence, and access to influential networks across government, investors, and industry.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                We advise and partner with startups, scaling companies, and established enterprises to navigate complexity and strengthen performance. Our team brings decades of leadership across finance, operations, marketing, real estate, global markets, and public-sector engagement—enabling us to guide clients through growth, turnaround, and transformation.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                We help leaders refine strategy, sharpen execution, and unlock opportunities that drive sustainable, scalable success.
              </p>
            </div>
            
            <div className="md:col-span-2">
              <div className="bg-white rounded-2xl p-8 shadow-sm">
                <div className="text-6xl text-[#c9a961] mb-4">"</div>
                <p className="text-xl font-serif italic text-gray-800 leading-relaxed">
                  A strategic advisory team with deep expertise in finance, operations, marketing, and global markets—guiding clients through growth and transformation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-serif font-bold text-gray-900 mb-6">Our Expertise</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Comprehensive strategic guidance across all dimensions of business growth, operational excellence, and organizational transformation.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <div
                key={index}
                onMouseEnter={() => setActiveService(index)}
                onMouseLeave={() => setActiveService(null)}
                className={`bg-[#f0f0f0] rounded-2xl p-6 transition-all duration-300 cursor-pointer ${
                  activeService === index ? 'bg-white shadow-xl -translate-y-1' : ''
                }`}
              >
                <div className="w-12 h-12 flex items-center justify-center mb-4">
                  <i className={`${service.icon} text-3xl text-[#c9a961]`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3 leading-tight">{service.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section id="case-studies" className="py-24 bg-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-5xl font-serif font-bold text-white mb-16">Case Studies</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {caseStudies.map((study, index) => (
              <div key={index} className="bg-white rounded-3xl overflow-hidden shadow-xl">
                <div className="h-80 w-full">
                  <img src={study.image} alt={study.title} className="w-full h-full object-cover object-top" />
                </div>
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{study.title}</h3>
                  <p className="text-sm text-gray-500 mb-4">
                    <strong>Lead Partner:</strong> {study.lead}
                  </p>
                  <div className="w-10 h-px bg-gray-300 mb-6"></div>
                  
                  <div className="mb-6">
                    <h4 className="text-xs uppercase tracking-wider text-gray-500 mb-2 font-medium">Overview</h4>
                    <p className="text-base text-gray-700 leading-relaxed">{study.overview}</p>
                  </div>
                  
                  <div className="mb-6">
                    <h4 className="text-xs uppercase tracking-wider text-gray-500 mb-3 font-medium">Key Contributions</h4>
                    <ul className="space-y-2">
                      {study.contributions.map((contribution, idx) => (
                        <li key={idx} className="flex items-start">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#c9a961] mt-2 mr-3 flex-shrink-0"></div>
                          <span className="text-sm text-gray-700">{contribution}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  {study.outcomes && (
                    <div>
                      <h4 className="text-xs uppercase tracking-wider text-gray-500 mb-3 font-medium">Outcomes</h4>
                      <ul className="space-y-2">
                        {study.outcomes.map((outcome, idx) => (
                          <li key={idx} className="flex items-start">
                            <div className="w-1.5 h-1.5 rounded-full bg-[#c9a961] mt-2 mr-3 flex-shrink-0"></div>
                            <span className="text-sm text-gray-700">{outcome}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section id="team" className="py-24 bg-[#f8f7f5]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-serif font-bold text-gray-900 mb-6">Partners</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our partners bring decades of leadership experience across finance, operations, marketing, real estate, and public-sector engagement.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {partners.map((partner, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                <div className="p-6">
                  <div className="w-40 h-40 mx-auto mb-6 rounded-full overflow-hidden border-4 border-[#c9a961]/20">
                    <img src={partner.image} alt={partner.name} className="w-full h-full object-cover object-top" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 text-center mb-2">{partner.name}</h3>
                  <p className="text-sm text-gray-500 italic text-center mb-4">{partner.title}</p>
                  
                  <div className="text-sm text-gray-700 leading-relaxed">
                    {expandedPartner === index ? (
                      <div>
                        <ul className="space-y-2">
                          {partner.bio.map((item, idx) => (
                            <li key={idx} className="flex items-start">
                              <div className="w-1 h-1 rounded-full bg-[#c9a961] mt-2 mr-2 flex-shrink-0"></div>
                              <span className="text-xs">{item}</span>
                            </li>
                          ))}
                        </ul>
                        <button
                          onClick={() => setExpandedPartner(null)}
                          className="text-[#c9a961] text-xs font-medium mt-4 hover:underline whitespace-nowrap"
                        >
                          Show Less
                        </button>
                      </div>
                    ) : (
                      <div>
                        <p className="text-xs line-clamp-2">{partner.bio[0]}</p>
                        <button
                          onClick={() => setExpandedPartner(index)}
                          className="text-[#c9a961] text-xs font-medium mt-2 hover:underline whitespace-nowrap"
                        >
                          Read More
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners & Networks Section */}
      <section id="strategic-networks" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-serif font-bold text-gray-900 mb-6">Strategic Networks</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Access to influential networks across government, investors, and industry leaders that open doors and create opportunities for our clients.
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16">
            {[
              'San Francisco Consul Corps',
              'GlobalSF',
              'California GO-Biz and China Trade & Investment Network',
              'Silicon Valley AREAA',
              'National Asian Pacific Center on Aging',
              'AAPI Lead',
              'USF Center for Asia Pacific Studies',
              'Bay Area Angels',
              'CAMACOL'
            ].map((org, index) => (
              <div key={index} className="flex items-center justify-center p-6 bg-gray-50 rounded-xl hover:bg-[#c9a961]/10 transition-colors duration-300">
                <span className="text-sm font-medium text-gray-700 text-center">{org}</span>
              </div>
            ))}
          </div>
          
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-base text-gray-700 leading-relaxed">
              Our partners maintain active leadership roles in premier business, civic, and industry organizations. These relationships provide our clients with strategic introductions, market intelligence, and access to decision-makers across sectors—accelerating growth and opening pathways that would otherwise remain closed.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16">
            <div className="text-white">
              <h2 className="text-5xl font-serif font-bold mb-8">Let's Connect</h2>
              <p className="text-lg text-gray-300 mb-12 leading-relaxed">
                Ready to navigate complexity and unlock new opportunities? Reach out to discuss how we can support your strategic objectives.
              </p>
              
              <div className="space-y-6 mb-12">
                <div className="flex items-start space-x-4">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-mail-line text-xl text-[#c9a961]"></i>
                  </div>
                  <a href="mailto:andy@accesspointadv.com" className="text-[#c9a961] hover:underline">
                    info@accesspointadv.com
                  </a>
                </div>
              </div>
              
              <div className="flex space-x-4">
                <a href="https://www.linkedin.com/company/access-point-advisors-llc/about/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 flex items-center justify-center border-2 border-white rounded-full hover:bg-[#c9a961] hover:border-[#c9a961] transition-all duration-300">
                  <i className="ri-linkedin-line text-xl"></i>
                </a>
              </div>
            </div>
            
            <div className="bg-white rounded-3xl p-8">
              <form 
                className="space-y-6" 
                data-readdy-form 
                id="contact-form"
                onSubmit={async (e) => {
                  e.preventDefault();
                  const form = e.currentTarget;
                  const formData = new FormData(form);
                  const submitButton = form.querySelector('button[type="submit"]') as HTMLButtonElement;
                  const originalText = submitButton.textContent;
                  
                  try {
                    submitButton.disabled = true;
                    submitButton.textContent = 'Sending...';
                    
                    const response = await fetch('https://readdy.ai/api/form/d5jvdlpugl990ce159ag', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                      },
                      body: new URLSearchParams(formData as any).toString(),
                    });
                    
                    if (response.ok) {
                      // Also send email notification
                      const emailData = {
                        to: 'andy@accesspointadv.com',
                        subject: `New Contact Form Submission from ${formData.get('name')}`,
                        message: `
Name: ${formData.get('name')}
Email: ${formData.get('email')}
Company: ${formData.get('company') || 'Not provided'}

Message:
${formData.get('message')}
                        `.trim()
                      };
                      
                      // Send to email endpoint
                      fetch('https://readdy.ai/api/form/d5jvdlpugl990ce159ag', {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/x-www-form-urlencoded',
                        },
                        body: new URLSearchParams({
                          ...Object.fromEntries(formData),
                          _to: 'andy@accesspointadv.com'
                        } as any).toString(),
                      });
                      
                      submitButton.textContent = 'Message Sent!';
                      submitButton.className = 'w-full bg-green-600 text-white py-4 rounded-lg font-medium transition-colors duration-300 whitespace-nowrap';
                      form.reset();
                      setTimeout(() => {
                        submitButton.textContent = originalText;
                        submitButton.className = 'w-full bg-[#c9a961] text-white py-4 rounded-lg font-medium hover:bg-[#b89851] transition-colors duration-300 whitespace-nowrap';
                        submitButton.disabled = false;
                      }, 3000);
                    } else {
                      throw new Error('Submission failed');
                    }
                  } catch (error) {
                    submitButton.textContent = 'Failed. Try Again';
                    submitButton.className = 'w-full bg-red-600 text-white py-4 rounded-lg font-medium transition-colors duration-300 whitespace-nowrap';
                    setTimeout(() => {
                      submitButton.textContent = originalText;
                      submitButton.className = 'w-full bg-[#c9a961] text-white py-4 rounded-lg font-medium hover:bg-[#b89851] transition-colors duration-300 whitespace-nowrap';
                      submitButton.disabled = false;
                    }, 3000);
                  }
                }}
              >
                <div>
                  <input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    required
                    className="w-full px-0 py-3 border-0 border-b-2 border-gray-200 focus:border-[#c9a961] outline-none transition-colors duration-300 text-gray-900"
                  />
                </div>
                <div>
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    required
                    className="w-full px-0 py-3 border-0 border-b-2 border-gray-200 focus:border-[#c9a961] outline-none transition-colors duration-300 text-gray-900"
                  />
                </div>
                <div>
                  <input
                    type="text"
                    name="company"
                    placeholder="Company Name"
                    className="w-full px-0 py-3 border-0 border-b-2 border-gray-200 focus:border-[#c9a961] outline-none transition-colors duration-300 text-gray-900"
                  />
                </div>
                <div>
                  <textarea
                    name="message"
                    placeholder="How can we help you?"
                    rows={4}
                    maxLength={500}
                    required
                    className="w-full px-0 py-3 border-0 border-b-2 border-gray-200 focus:border-[#c9a961] outline-none transition-colors duration-300 resize-none text-gray-900"
                  ></textarea>
                  <p className="text-xs text-gray-400 mt-1">Maximum 500 characters</p>
                </div>
                <button
                  type="submit"
                  className="w-full bg-[#c9a961] text-white py-4 rounded-lg font-medium hover:bg-[#b89851] transition-colors duration-300 whitespace-nowrap"
                >
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1a1a1a] py-8 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <div className="flex items-center">
              <img src="https://public.readdy.ai/ai/img_res/a3e9a384-3fd2-411f-b961-06e957112ff2.png" alt="Access Point Advisors LLC" className="h-10" />
            </div>
            
            <div className="flex flex-wrap items-center justify-center gap-6">
              {['About', 'Services', 'Case Studies', 'Team', 'Strategic Networks', 'Contact'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item.toLowerCase().replace(' ', '-'))}
                  className="text-sm text-gray-400 hover:text-[#c9a961] transition-colors duration-300 whitespace-nowrap"
                >
                  {item}
                </button>
              ))}
            </div>
            
            <div className="flex items-center space-x-4">
              <p className="text-sm text-gray-500">© 2025 Access Point Advisors LLC</p>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-xs text-gray-600 hover:text-[#c9a961] transition-colors duration-300 whitespace-nowrap">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}